import { Button } from '@mm/components';
import { useViewActions } from '@mm/state/actions/viewActions';
import { MyLeadsSearchQuery } from '@mm/state/interfaces/myLeads';
import { isBilling2023V2Subscription } from '@mm/state/model/SubscriptionModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectActiveFacebookPage } from '@mm/state/selectors/facebookPageSelector';
import { Divider } from 'antd';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useImportContactsPermission } from '../../../../../common/hooks/use-import-contacts-permission';
import styles from './Contacts.module.css';
import { EnrichButton } from './EnrichButton/EnrichButton';
import { MyLeadsContactsTable } from './MyLeadsContactsTable/MyLeadsContactsTable';
import { MyLeadsFilterBar } from './MyLeadsFilterBar/MyLeadsFilterBar';
import { MyLeadsFiltersPanel } from './MyLeadsFiltersPanel/MyLeadsFiltersPanel';
import { useFilterParams } from './MyLeadsFiltersPanel/use-filter-params';
import { useFilterMyLeads } from './use-filter-my-leads';
import { useDummyContacts } from './use-dummy-contacts';
import { selectContacts } from '@mm/state/selectors/contactsSelector';

export const Contacts = () => {
  const location = useLocation<MyLeadsSearchQuery>();

  const [filterParams, setFilterParams] = useState<MyLeadsSearchQuery>(
    location.state || {},
  );
  useEffect(() => {
    setFilterParams(location.state || {});
  }, [location.state?.query]);

  const { busy } = useFilterMyLeads({
    fetchAttributes: true,
    filterParams,
  });

  const { openImportContactListModal, openAccountLockedModal } =
    useModalActions();
  const { showSlideoutMenu } = useViewActions();
  const uploadContactsPermission = useImportContactsPermission();
  const onImportClick = () => openImportContactListModal();
  const facebookPage = useSelector(selectActiveFacebookPage);
  const dummyLeads = useDummyContacts();
  const { pagination } = useSelector(selectContacts);
  const [totalContacts, setTotalContacts] = useState({
    value: dummyLeads.length,
    loaded: false,
  });
  const { hasFilters } = useFilterParams({ filterParams });

  useEffect(() => {
    // Set total contacts only once when my leads page is loaded at the first time
    if (!totalContacts.loaded && pagination.total > 0) {
      setTotalContacts({ value: pagination.total, loaded: true });
    }
  }, [pagination]);

  const processClick = (onSuccess: (args?: any) => void) => {
    if (
      facebookPage?.subscription?.failedPayment &&
      isBilling2023V2Subscription(facebookPage?.subscription)
    ) {
      openAccountLockedModal();
    } else {
      onSuccess();
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        All My Leads — {totalContacts.value?.toLocaleString('en-US')}
        <div className={styles.buttonsContainer}>
          <EnrichButton />
          <Button
            css={{ container: styles.button }}
            disabled={!uploadContactsPermission.isAllowed}
            color="secondary"
            onClick={() => processClick(onImportClick)}
          >
            Import
          </Button>
          <Button
            color="secondary"
            onClick={() => showSlideoutMenu('attributesAndTags')}
            css={{ container: styles.button }}
          >
            Attributes and Tags
          </Button>
        </div>
      </div>

      <div className={styles.content}>
        <MyLeadsFiltersPanel
          filterParams={filterParams}
          setFilterParams={setFilterParams}
          busy={busy}
        />
        <div className={styles.contacts}>
          <MyLeadsFilterBar
            filterParams={filterParams}
            setFilterParams={setFilterParams}
          />

          {hasFilters && <Divider style={{ margin: '12px 0px 22px' }} />}

          <MyLeadsContactsTable
            filterParams={filterParams}
            setFilterParams={setFilterParams}
            isFetchingContacts={busy}
            processClick={processClick}
          />
        </div>
      </div>
    </div>
  );
};
